// Worker-facing site induction runner (Phase 3) — read, knowledge check, declaration, sign.
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSafetyNavigate as useNavigate } from "@/lib/safetyNavigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SignaturePad } from "@/components/SignaturePad";
import {
  ArrowLeft, Loader2, ShieldCheck, MapPin, Flame, AlertTriangle, HardHat,
  ClipboardList, Truck, Leaf, Megaphone, CheckCircle2, PenLine, Info,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ICONS = {
  welcome: Info, emergency: Flame, site_rules: ClipboardList, hazards: AlertTriangle,
  ppe: HardHat, plant_traffic: Truck, hazardous_substances: ShieldCheck,
  environment: Leaf, reporting: Megaphone,
};
const LABELS = {
  welcome: "Welcome", emergency: "Emergency", site_rules: "Site Rules",
  hazards: "Hazards & Controls", ppe: "Required PPE", plant_traffic: "Plant & Traffic",
  hazardous_substances: "Hazardous Substances", environment: "Environment", reporting: "Reporting",
};
const FIELD_LABELS = {
  body: "", scope: "Scope of works", access: "Site access", parking: "Parking",
  restricted_areas: "Restricted areas", emergency_contact: "Emergency contact",
  muster_point: "Muster point", first_aid_location: "First aid", first_aiders: "First aiders",
  fire_equipment: "Fire equipment", emergency_access: "Emergency access",
  nearest_medical: "Nearest medical", evacuation: "Evacuation",
};
const fmt = (d) => { try { return new Date(d).toLocaleString("en-NZ", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

export default function SiteInductionRunner() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [participantName, setParticipantName] = useState("");
  const [company, setCompany] = useState("");
  const [signing, setSigning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doneRec, setDoneRec] = useState(null);

  useEffect(() => {
    let ok = true;
    api.get(`/site-inductions/${id}/deliver`)
      .then((r) => { if (!ok) return; setData(r.data); setParticipantName(r.data.participant?.name || ""); })
      .catch((e) => toast.error(e.response?.data?.detail || "This induction isn't available"))
      .finally(() => ok && setLoading(false));
    return () => { ok = false; };
  }, [id]);

  const content = data?.revision?.content || {};
  const kc = content.knowledge_check || {};
  const questions = kc.enabled ? (kc.questions || []) : [];
  const declaration = (content.declaration || {}).text || "";

  const visibleSections = useMemo(() => {
    const order = ["welcome", "emergency", "site_rules", "hazards", "ppe", "plant_traffic", "environment", "reporting"];
    return order.filter((k) => hasContent(k, content[k]));
  }, [content]);

  const submit = async (signature) => {
    setSigning(false);
    if (!signature) return;
    if (!accepted) { toast.error("Please tick the declaration first"); return; }
    setBusy(true);
    try {
      const answerArr = questions.map((_, i) => answers[i] || "");
      const { data: res } = await api.post(`/site-inductions/${id}/complete`, {
        signature, declaration_accepted: true, answers: answerArr,
        participant_name: participantName || undefined, company: company || undefined,
      });
      setDoneRec(res);
      toast.success("Induction complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit induction");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="py-24 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF5F15]" /></div>;
  if (!data) return <div className="py-24 text-center text-sm text-zinc-500">This induction isn't published yet.</div>;

  if (doneRec) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center px-4" data-testid="si-runner-done">
        <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4"><CheckCircle2 className="w-9 h-9 text-emerald-600" /></div>
        <h1 className="text-2xl font-black tracking-tight">You're inducted</h1>
        <p className="text-sm text-zinc-500 mt-1">{data.induction.site_name} · {data.induction.job_number}</p>
        {doneRec.knowledge_result && (
          <p className="text-sm mt-3 font-bold">Knowledge check: {doneRec.knowledge_result.correct}/{doneRec.knowledge_result.total} acknowledged</p>
        )}
        <p className="text-xs text-zinc-400 mt-4">Your signed induction record has been saved for the site. Follow the site rules and report any hazards.</p>
        <Button onClick={() => nav("/app/safety/site-inductions")} className="mt-6 rounded-lg bg-[#09090B] text-white h-10 font-bold uppercase text-[11px] tracking-wider" data-testid="si-runner-finish">Done</Button>
      </div>
    );
  }

  const already = data.my_completion?.is_current;

  return (
    <div className="max-w-2xl mx-auto pb-16" data-testid="si-runner">
      <button onClick={() => nav(-1)} className="text-xs text-zinc-500 hover:text-zinc-800 flex items-center gap-1 mb-3" data-testid="si-runner-back"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>

      <div className="rounded-2xl bg-[#09090B] text-white p-6 mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF5F15] mb-1">Site Induction</div>
        <h1 className="text-2xl font-black tracking-tight leading-tight">{data.induction.site_name}</h1>
        <div className="text-sm text-zinc-300 mt-1">{data.induction.job_number}{data.induction.client ? ` · ${data.induction.client}` : ""}</div>
        <div className="text-[11px] text-zinc-400 mt-2">{data.org_name} · REV {String(data.revision.revision_number).padStart(2, "0")}</div>
      </div>

      {already && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 p-3 text-sm mb-4 flex items-center gap-2" data-testid="si-runner-already">
          <CheckCircle2 className="w-4 h-4" /> You've already completed the current version of this induction. You can re-sign below if needed.
        </div>
      )}

      <SiteInfoCard info={content.site_information} />

      {visibleSections.map((k) => (
        <SectionCard key={k} sectionKey={k} value={content[k]} />
      ))}

      {questions.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid="si-runner-kc">
          <h2 className="font-black text-lg mb-1 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#FF5F15]" /> Knowledge check</h2>
          <p className="text-xs text-zinc-400 mb-4">Answer to confirm you understand the key points.</p>
          <div className="space-y-4">
            {questions.map((q, i) => (
              <div key={i} data-testid={`si-runner-q-${i}`}>
                <label className="text-sm font-bold block mb-1.5">{i + 1}. {q.q}</label>
                <Input value={answers[i] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))} placeholder="Your answer" className="rounded-lg h-10" data-testid={`si-runner-a-${i}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid="si-runner-declaration">
        <h2 className="font-black text-lg mb-3 flex items-center gap-2"><PenLine className="w-5 h-5 text-[#FF5F15]" /> Declaration</h2>
        <p className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{declaration}</p>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Your name</label>
            <Input value={participantName} onChange={(e) => setParticipantName(e.target.value)} className="rounded-lg h-10" data-testid="si-runner-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 block mb-1">Company (optional)</label>
            <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Subcontractor / employer" className="rounded-lg h-10" data-testid="si-runner-company" />
          </div>
        </div>

        <label className="flex items-start gap-2.5 mt-4 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF5F15]" data-testid="si-runner-accept" />
          <span>I confirm I have read and understood this induction and agree to the declaration above.</span>
        </label>

        <div className="mt-5">
          {signing ? (
            <SignaturePad onSave={submit} onCancel={() => setSigning(false)} testIdPrefix="si-runner-sig" />
          ) : (
            <Button onClick={() => { if (!accepted) return toast.error("Please tick the declaration first"); setSigning(true); }}
              disabled={busy} className="w-full h-12 rounded-lg bg-[#FF5F15] hover:bg-[#E65100] text-white font-black uppercase tracking-wider text-sm" data-testid="si-runner-sign">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><PenLine className="w-4 h-4 mr-2" /> Sign & complete induction</>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function hasContent(key, v) {
  if (key === "ppe" || key === "site_rules" || key === "hazards") return Array.isArray(v) && v.length > 0;
  if (v && typeof v === "object") return Object.values(v).some((x) => String(x || "").trim());
  return false;
}

function SiteInfoCard({ info }) {
  if (!info) return null;
  const rows = [
    ["Site", info.site_name], ["Address", info.site_address], ["Client", info.client],
    ["Project manager", info.project_manager], ["Site supervisor", info.site_supervisor],
    ["Site contact", info.site_contact], ["Working hours", info.working_hours],
  ].filter(([, v]) => String(v || "").trim());
  if (!rows.length) return null;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid="si-runner-siteinfo">
      <h2 className="font-black text-lg mb-3 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#FF5F15]" /> Site information</h2>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex flex-col"><dt className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{k}</dt><dd className="font-medium">{v}</dd></div>
        ))}
      </dl>
    </div>
  );
}

function SectionCard({ sectionKey, value }) {
  const Icon = ICONS[sectionKey] || Info;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5 mb-4" data-testid={`si-runner-sec-${sectionKey}`}>
      <h2 className="font-black text-lg mb-3 flex items-center gap-2"><Icon className="w-5 h-5 text-[#FF5F15]" /> {LABELS[sectionKey]}</h2>
      <SectionBody sectionKey={sectionKey} value={value} />
    </div>
  );
}

function SectionBody({ sectionKey, value }) {
  if (sectionKey === "ppe") {
    return (
      <div className="flex flex-wrap gap-2">
        {(value || []).map((p, i) => <span key={i} className="px-3 py-1.5 rounded-full bg-[#FF5F15]/10 text-[#E65100] text-sm font-bold">{p}</span>)}
      </div>
    );
  }
  if (sectionKey === "site_rules") {
    return <ul className="space-y-2">{(value || []).map((r, i) => <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {r.title || r}</li>)}</ul>;
  }
  if (sectionKey === "hazards") {
    return (
      <div className="space-y-3">
        {(value || []).map((h, i) => (
          <div key={i} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <div className="font-bold text-sm text-amber-900 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {h.name}</div>
            {h.controls && <div className="text-sm text-zinc-700 mt-1">{h.controls}</div>}
          </div>
        ))}
      </div>
    );
  }
  // field/dict sections
  const v = value || {};
  const entries = Object.entries(v).filter(([, val]) => String(val || "").trim());
  return (
    <div className="space-y-2.5">
      {entries.map(([k, val]) => (
        <div key={k}>
          {FIELD_LABELS[k] ? <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-0.5">{FIELD_LABELS[k]}</div> : null}
          <div className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{val}</div>
        </div>
      ))}
    </div>
  );
}
